import { eventIconLabel, eventThemePresetLabel, tiptapDocToPlainText } from '@odo/shared-ui';
import type { EventDraft } from './eventDraft';
import { ANNOUNCEMENT_DURATION_HOURS, eventKindLabel } from './eventKind';

// Mirrors `EventAlignmentPicker`'s own labels. Kept local rather than
// imported from that picker file - pulling a plain constant out of a
// component file trips `react-refresh/only-export-components`, and the map
// is two entries, not worth a third shared file over.
const ALIGNMENT_LABELS: Record<string, string> = {
    left: 'Left',
    center: 'Centered'
};

// One-line, muted summaries shown on a folded section's header (see
// `EventFormSection`). Every one of these reads only the live `draft`, so
// they stay in sync with whatever the author last typed - there is no
// separate "committed" copy to fall out of step with.

const NOTHING_SET = '-';
const TITLE_TRUNCATE_LENGTH = 40;
const BADGE_TAG_TRUNCATE_LENGTH = 20;

function truncate(text: string, max: number): string {
    return text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;
}

/** "17 Jun, 09:00" from a `datetime-local` value. Already the author's own
 *  wall clock (see utils/eventDateTime.ts) - no zone conversion needed here,
 *  unlike `EventDateTimeField`'s zone-label caption. */
function formatLocalStart(local: string): string | null {
    const [datePart, timePart] = local.split('T');
    if (!datePart || !timePart) return null;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute] = timePart.split(':').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1, hour || 0, minute || 0);
    if (Number.isNaN(date.getTime())) return null;
    const dayMonth = date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
    const time = date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
    return `${dayMonth}, ${time}`;
}

function formatDuration(hours: number): string | null {
    if (!Number.isFinite(hours) || hours <= 0) return null;
    if (hours < 1) return `${Math.round(hours * 60)} min`;
    return `${Math.round(hours * 100) / 100}h`;
}

/** Kind + when + how long, e.g. "Meeting · 17 Jun, 09:00 · 1h". Falls back to
 *  just the kind label until a start time is entered. */
export function basicsSectionSummary(draft: EventDraft, isAnnouncement: boolean): string {
    const kind = eventKindLabel(draft.kind);
    const when = formatLocalStart(draft.startsAtLocal);
    if (!when) return kind;
    const hours = isAnnouncement ? ANNOUNCEMENT_DURATION_HOURS : Number(draft.durationHours);
    const duration = formatDuration(hours);
    return duration ? `${kind} · ${when} · ${duration}` : `${kind} · ${when}`;
}

/** True once the pop-up's title/subtitle/visibility diverges from what it
 *  mirrors off the banner - see `EventPopupSection`'s mirror effects, which
 *  this only reads the resulting draft of rather than duplicating. */
function isPopupOverridden(draft: EventDraft): boolean {
    const modalTitle = draft.modalTitle.trim();
    if (modalTitle && modalTitle !== draft.title.trim()) return true;
    if (!draft.modalShowSubtitle) return true;
    const modalSubtitleText = draft.modalSubtitleDoc
        ? tiptapDocToPlainText(draft.modalSubtitleDoc).trim()
        : '';
    return Boolean(modalSubtitleText) && modalSubtitleText !== draft.subtitle.trim();
}

/** e.g. `"Case of the Month" · badge NEW · pop-up overridden`. */
export function contentSectionSummary(draft: EventDraft): string {
    const title = draft.title.trim();
    const parts = [title ? `"${truncate(title, TITLE_TRUNCATE_LENGTH)}"` : 'Untitled'];
    if (draft.badgeTag.trim()) {
        parts.push(`badge ${truncate(draft.badgeTag.trim(), BADGE_TAG_TRUNCATE_LENGTH)}`);
    }
    if (isPopupOverridden(draft)) parts.push('pop-up overridden');
    return parts.join(' · ');
}

/** "Every practice", or "3 practices". The count is what an author needs at a
 *  glance; which ones is a click away in the section itself. */
export function audienceSectionSummary(draft: EventDraft): string {
    if (draft.audienceMode === 'all') return 'Every practice';
    const count = draft.practices.length;
    // Only reachable while the draft is invalid (validation requires at least
    // one), so it reads as the prompt it is rather than as a settled state.
    if (count === 0) return 'No practices selected';
    return count === 1 ? '1 practice' : `${count} practices`;
}

/** e.g. `ODO Teal · Tooth · Centered`. */
export function appearanceSectionSummary(draft: EventDraft): string {
    const theme = eventThemePresetLabel(draft.themePreset);
    const icon = draft.iconKey ? eventIconLabel(draft.iconKey) : 'No icon';
    const alignment = ALIGNMENT_LABELS[draft.alignment] ?? draft.alignment;
    return `${theme} · ${icon} · ${alignment}`;
}

/** `zoomUrl` is write-only - `draft.zoomUrl` starts blank on every edit even
 *  though the event may already have one stored (see eventDraft.ts). A blank
 *  value on edit therefore reads as "unchanged", not "none" - only a create
 *  draft with nothing typed anywhere earns the `-` fallback. */
export function linksSectionSummary(draft: EventDraft, isEdit: boolean): string {
    const parts: string[] = [];
    const zoomUrl = draft.zoomUrl.trim();
    if (zoomUrl) {
        parts.push('Zoom link set');
    } else if (isEdit) {
        parts.push('Zoom link unchanged');
    }
    if (draft.googleCalendarUrl.trim()) parts.push('Calendar');
    if (draft.icsUrl.trim()) parts.push('.ics');
    return parts.length ? parts.join(' · ') : NOTHING_SET;
}
